import type { CustomerDocument } from '../hooks/useDocuments';
import { useI18n } from '../i18n/I18nProvider';
import { ResourceMark } from './ResourceMark';

/**
 * Where a document row lives. Uploaded files show their size; documents
 * mirrored from PazNet Learn show the Learn mark and name instead, since a
 * linked article has no file size and the reader should know the link leaves
 * the portal.
 */
export function DocumentOrigin({ doc, fileSize }: { doc: Pick<CustomerDocument, 'source' | 'file_size_bytes'>; fileSize: string }) {
  const { t } = useI18n();
  if (doc.source === 'zoho_learn') {
    return (
      <span className="inline-flex items-center gap-1 align-middle">
        <ResourceMark kind="learn" size="sm" />
        {t('documents.source.zoho_learn')}
      </span>
    );
  }
  return <span>{fileSize}</span>;
}

/**
 * "Open" for a document whose content is a link (PazNet Learn article
 * sharing link). Opens in a new tab; the arrow says so. Sits where the
 * Download button sits for uploaded files so the row reads the same.
 */
export function DocumentOpenLink({ href, compact = false }: { href: string; compact?: boolean }) {
  const { t } = useI18n();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={t('documents.opens_in_new_tab')}
      className={`inline-flex min-h-11 flex-shrink-0 items-center gap-1 px-2 text-sm font-medium text-primary hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        compact ? '' : 'rounded-md'
      }`}
    >
      {t('documents.open')}
      <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M7 7h10v10" />
      </svg>
    </a>
  );
}
